// proofPhoto.js
// -----------------------------------------------------------------------------
// The rules for the photos a driver takes at a customer's home, as pure
// functions: at which stamps, how many, how large, where they are stored, and
// when a sheet may be saved.
//
// Three stamps collect photos, in visit order -- the front of the house on
// ถึงบ้านลูกค้า (the driver was at this address), the room on เริ่มติดตั้ง (what it
// looked like before the team touched it) and the finished work on เสร็จงาน.
// Together they are proof the furniture was installed at the customer's home,
// not a catalogue shot. A phone camera hands over 3-5 MB at 12 MP; shrunk to a
// 1280 px long edge at JPEG quality 0.7 it is roughly 150-300 KB and still shows
// the room and the item plainly. The shrinking itself needs a canvas and lives in
// driver/photoCapture.vue; the numbers it obeys live here, beside the storage
// rule's limits they must stay inside.
//
// Pure: no Vue, no Firestore, CommonJS, so scripts/check-driver-day.js can
// require() it -- the same arrangement stationReport.js keeps.
// -----------------------------------------------------------------------------

const MAX_EDGE = 1280
// Tried in order until the result fits under MAX_BYTES. The first one fits for
// every ordinary photo; the second exists for a very detailed scene, so a
// driver is never told a photo is "too big" -- a thing they cannot act on.
const QUALITIES = [0.7, 0.5]
// storage.rules refuses a photo at 1 MB. A shrunk photo is a fraction of that,
// so the cap only ever stops something that did not go through the shrink.
const MAX_BYTES = 1024 * 1024

// Why there is no photo. 'ระบบขัดข้อง' is kept apart from the customer's refusal
// for the same reason the signature's reasons are: "the app broke" and "the
// customer said no" must not read the same to whoever looks later.
const SKIP_REASONS = ['ลูกค้าไม่อนุญาตให้ถ่ายรูป', 'ระบบขัดข้อง']

// The one table of which stamp collects which photos. The sheet on stop.vue,
// the upload paths, the Firestore fields on deliveryStop.proof, the monitor
// board's dialog and the pattern storage.rules must accept are all read from
// here, so adding a stage or changing a limit is one edit.
//
// `file` is the storage name: `<file>-1.jpg` .. `<file>-<max>.jpg`. The finish
// stage keeps `photo` and `photoUrls`, the names it shipped with in 1.22.0, so
// the proof of every job finished before the other two stages existed still
// reads -- and still displays -- as the after-installation photos it is.
//
// Each stage writes only its own two fields, so the house photo taken at 10:00
// can never be blanked by the finish sheet saved at 11:30, or the reverse.
const STAGES = {
  arrived: {
    file: 'house', max: 1, title: 'รูปหน้าบ้านลูกค้า', short: 'หน้าบ้าน',
    urlsField: 'housePhotoUrls', skipField: 'housePhotoSkippedReason'
  },
  installStarted: {
    file: 'before', max: 3, title: 'รูปพื้นที่ก่อนติดตั้ง', short: 'ก่อนติดตั้ง',
    absentTitle: 'รูปจุดที่จะวางสินค้า',
    urlsField: 'beforePhotoUrls', skipField: 'beforePhotoSkippedReason'
  },
  finished: {
    file: 'photo', max: 5, title: 'รูปงานที่ติดตั้ง', short: 'หลังติดตั้ง',
    absentTitle: 'รูปสินค้าที่วางไว้',
    urlsField: 'photoUrls', skipField: 'photoSkippedReason'
  },
  // The paper ใบส่งของ the customer signs. It is NOT a stamp of its own: it is
  // taken on the เสร็จงาน sheet, after the photos of the work and the signature
  // on the phone, because drivers reported that customers sign the paper last
  // -- after บันทึก, when there used to be nowhere left to photograph it. It is
  // required (a photo or a reason), like every other stage, and the reasons
  // are its own: "the customer would not allow a photo" makes no sense of a
  // document the company printed.
  receipt: {
    file: 'receipt', max: 3, title: 'รูปใบส่งของที่ลูกค้าเซ็นแล้ว', short: 'ใบส่งของ',
    absentTitle: 'รูปใบส่งของที่วางไว้กับสินค้า',
    urlsField: 'receiptPhotoUrls', skipField: 'receiptPhotoSkippedReason',
    reasons: ['ไม่มีใบส่งของมากับงาน', 'ลูกค้าไม่อยู่', 'ระบบขัดข้อง'],
    stamp: false
  },
  // Evidence for ส่งไม่สำเร็จ -- the defect, the wrong colour, the house with
  // nobody home. Taken on the rejection sheet, which records the `rejected`
  // stamp itself (driverDay.REJECTED), so it opens no stamp sheet either.
  rejected: {
    file: 'reject', max: 5, title: 'รูปหลักฐานที่ส่งไม่สำเร็จ', short: 'ส่งไม่สำเร็จ',
    urlsField: 'rejectPhotoUrls', skipField: 'rejectPhotoSkippedReason',
    stamp: false
  }
}

// Visit order, for anything that lists the stages -- the monitor dialog shows
// the house, then the room before, then the work after, then the signed paper
// -- or, for a stop that failed, the evidence of why.
const STAGE_ORDER = ['arrived', 'installStarted', 'finished', 'receipt', 'rejected']

// A stage by its key -- a stamp's step, or 'receipt' -- or null. Used for
// storage paths and saved fields, which every stage has.
function stageFor (step) {
  return Object.prototype.hasOwnProperty.call(STAGES, step) ? STAGES[step] : null
}

// The stage a STAMP opens a photo sheet for, or null for a stamp that takes
// none (ออกเดินทาง, which is still one tap). 'receipt' is a stage but not a
// stamp, so a stamp named after it can never open its sheet.
function stampStageFor (step) {
  const stage = stageFor(step)
  return stage && stage.stamp !== false ? stage : null
}

// What a stage's photos are called. `absent` is a visit with nobody home
// (consentForm.customerAbsent): the products were left at the house, not
// installed, so "the room before installing" is the spot they were left in and
// "the installed work" is the products left there. Same files, same fields --
// only the words change. A stage with no absentTitle keeps its own.
function titleFor (step, absent) {
  const stage = stageFor(step)
  if (!stage) return ''
  return (absent && stage.absentTitle) || stage.title
}

// The reasons a driver may give for a stage without a photo.
function reasonsFor (step) {
  const stage = stageFor(step)
  return (stage && stage.reasons) || SKIP_REASONS
}

// The dimensions to draw a photo at: the long edge brought down to `maxEdge`,
// the aspect ratio kept, and never enlarged. null for dimensions an <img> could
// not have decoded, so the caller reports a failed photo rather than drawing a
// 0x0 canvas and uploading an empty JPEG.
function targetSize (width, height, maxEdge) {
  const w = Number(width)
  const h = Number(height)
  const edge = Number(maxEdge == null ? MAX_EDGE : maxEdge)
  if (!(w > 0) || !(h > 0) || !(edge > 0)) return null
  const scale = Math.min(1, edge / Math.max(w, h))
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale))
  }
}

// Where photo `index` (0-based) of a stage is stored. Fixed names, so a retry
// after a failed upload replaces the file instead of piling up copies. A name
// outside the stage's limit is refused in storage.rules, so it is refused here
// first.
function storagePath (stop, step, index) {
  const s = stop || {}
  const stage = stageFor(step)
  const i = Number(index)
  if (!stage || !s.deliveryDate || !s.id) return ''
  if (!(i >= 0 && i < stage.max) || Math.floor(i) !== i) return ''
  return 'driver-proof/' + s.deliveryDate + '/' + s.id + '/' + stage.file + '-' + (i + 1) + '.jpg'
}

// The object-name pattern storage.rules must accept for photos, built from
// STAGES: house-1 | before-[1-3] | photo-[1-5] | receipt-[1-3] | reject-[1-5]. npm run test:driver asserts the
// rules file contains exactly this string, so a limit changed here and not
// there fails the test instead of a driver's upload.
function storageRulePattern () {
  const names = STAGE_ORDER.map(step => {
    const stage = STAGES[step]
    return stage.max === 1 ? stage.file + '-1' : stage.file + '-[1-' + stage.max + ']'
  })
  return 'driver-proof/[^/]+/[^/]+/(' + names.join('|') + ')[.]jpg'
}

// Why a stage's photos may not be saved yet, in the Thai the driver reads, or
// '' when they may.
function photoProblem (step, photoCount, skippedReason, absent) {
  const stage = stageFor(step)
  if (!stage) return ''
  const count = Number(photoCount) || 0
  if (count > stage.max) return 'ถ่ายรูปได้ไม่เกิน ' + stage.max + ' รูป'
  if (count === 0 && !skippedReason) {
    return 'ถ่าย' + titleFor(step, absent) + 'อย่างน้อย 1 รูป หรือเลือกเหตุผลที่ถ่ายรูปไม่ได้'
  }
  return ''
}

// What a stage writes onto deliveryStop.proof: its own two fields and nothing
// else. A reason is kept only when there is no photo, so a driver who picked
// one and then took a photo anyway is not recorded as having skipped.
function photoFields (step, urls, skippedReason) {
  const stage = stageFor(step)
  if (!stage) return {}
  const list = (urls || []).slice(0, stage.max)
  const out = {}
  out[stage.urlsField] = list
  out[stage.skipField] = list.length ? '' : (skippedReason || '')
  return out
}

// --- photos added after the job is closed ---
// A driver who forgot a photo -- the house, the room before, the finished work,
// the signed paper, the evidence for a failed delivery -- may add it once the
// stop is closed. Each stage keeps its own limit and file names, so nothing
// here changes what storage.rules accepts: an added photo takes the next free
// name (`photo-3.jpg` after two), never one already holding a photo.

// The stamp a stage's photos belong to. A stage is offered after the job only
// when the visit actually reached that stamp: a stop rejected at the door has
// no room-before to photograph, a failed one no signed paper.
const STAGE_STAMP = {
  arrived: 'arrived',
  installStarted: 'installStarted',
  finished: 'finished',
  receipt: 'finished',
  rejected: 'rejected'
}

function stamped (steps, step) {
  return !!(steps && steps[step] && steps[step].at != null)
}

// The stages a closed stop may still add photos to, in visit order, each with
// how many it holds and how many more it may take. [] for a stop not yet
// closed -- before then the stage sheets themselves take the photos.
// `absent` changes only the titles, as titleFor does.
function addableStages (stop, absent) {
  const s = stop || {}
  if (s.status !== 'done' && s.status !== 'failed') return []
  const proof = s.proof || {}
  return STAGE_ORDER
    .filter(step => stamped(s.steps, STAGE_STAMP[step]))
    .map(step => {
      const stage = STAGES[step]
      const have = (proof[stage.urlsField] || []).length
      return { step, title: titleFor(step, absent), short: stage.short, have, room: Math.max(0, stage.max - have) }
    })
}

// The list a stage holds once `added` is put after what it already had:
// duplicates dropped, cut at the stage's limit. Read inside the transaction
// that writes it, so a second phone adding at the same time is not lost.
function appendUrls (step, existing, added) {
  const stage = stageFor(step)
  if (!stage) return []
  const out = []
  ;(existing || []).concat(added || []).forEach(u => {
    if (u && out.indexOf(u) === -1) out.push(u)
  })
  return out.slice(0, stage.max)
}

// What an added photo writes onto deliveryStop.proof: the stage's list and one
// line in `lateAdds`, so staff can tell a photo taken at the door from one
// added afterwards. The stage's skip reason is left as it was -- it is the
// record of why there was no photo at the time.
function lateAddFields (step, existing, added, entry) {
  const stage = stageFor(step)
  if (!stage) return {}
  const out = {}
  out[stage.urlsField] = appendUrls(step, existing, added)
  out.lateAdds = ((entry && entry.prior) || []).concat([{
    step,
    count: out[stage.urlsField].length - (existing || []).length,
    at: (entry && entry.at) || '',
    by: (entry && entry.by) || ''
  }])
  return out
}

// The stages of a proof that had photos added after the job, for the monitor.
function lateAddedSteps (proof) {
  const list = (proof && proof.lateAdds) || []
  const out = []
  list.forEach(e => { if (e && e.step && e.count > 0 && out.indexOf(e.step) === -1) out.push(e.step) })
  return out
}

// The finish sheet is three pages, because two different people fill it in:
//   1. the driver photographs the installed work      photoProblem('finished')
//   2. the customer rates, signs and presses บันทึก    customerProblem
//   3. the driver photographs the signed ใบส่งของ and   photoProblem('receipt')
//      flags anything needing follow-up, then closes  + deliveryOutcome.followUpProblem
// Customers asked to press บันทึก themselves, so page 2 is theirs alone, and the
// paper -- which they sign after the phone -- is the driver's job on page 3.

// Why the customer's page may not be saved yet, or ''. A rating is required
// only from a customer who signed -- one who was not there cannot give one --
// and "ไม่ประสงค์ให้คะแนน" is a real answer, kept apart from 0 so a driver is
// never pushed into tapping the stars themselves.
function customerProblem (input) {
  const p = input || {}
  if (!p.hasSignature && !p.refusedReason) {
    return 'กรุณาเซ็นชื่อ หรือเลือกเหตุผลที่เซ็นไม่ได้'
  }
  if (p.hasSignature && !(Number(p.rating) >= 1) && !p.ratingDeclined) {
    return 'กรุณาให้คะแนนความพึงพอใจ หรือเลือก "ไม่ประสงค์ให้คะแนน"'
  }
  return ''
}

module.exports = {
  titleFor,
  MAX_EDGE,
  QUALITIES,
  MAX_BYTES,
  SKIP_REASONS,
  STAGES,
  STAGE_ORDER,
  stageFor,
  stampStageFor,
  reasonsFor,
  targetSize,
  storagePath,
  storageRulePattern,
  photoProblem,
  photoFields,
  customerProblem,
  addableStages,
  appendUrls,
  lateAddFields,
  lateAddedSteps
}
